import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, Search, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { useOnboardingPeople } from '../../hooks/onboardingOps';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonList } from '../../components/ui/Skeleton';
import { formatDate } from '../../lib/dateFmt';

/**
 * Read-only Submissions area: pick a person to see everything they've
 * submitted or started in one place, without opening the editable record.
 * The list is scoped to people with onboarding activity (assigned tasks or a
 * started draft) so it stays relevant.
 */
export function OnboardingSubmissions() {
  const people = useOnboardingPeople();
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (people.data ?? [])
      .filter((p) => !p.hidden && (p.total > 0 || p.has_started))
      .filter(
        (p) =>
          !term ||
          p.full_name.toLowerCase().includes(term) ||
          p.email.toLowerCase().includes(term),
      );
  }, [people.data, q]);

  return (
    <main className="mx-auto max-w-4xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={Inbox}
        eyebrow="Onboarding"
        title="Submissions"
        description="See what each person has submitted or started, all in one place."
        trailing={
          <a href="/api/admin/onboarding/submissions.xlsx">
            <Button variant="ghost" size="sm" iconLeft={<FileSpreadsheet className="h-4 w-4" />}>
              Export all
            </Button>
          </a>
        }
      />

      <Card>
        <div className="flex items-center gap-2 px-4 py-3">
          <Search className="h-4 w-4 text-fg-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email"
            className="w-full bg-transparent text-sm text-fg outline-none placeholder:text-fg-dim"
          />
        </div>
      </Card>

      {people.isLoading ? (
        <SkeletonList rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title="No submissions yet"
          description="People appear here once they have onboarding tasks assigned or have started a form."
        />
      ) : (
        <Card>
          <ul className="divide-y divide-divider/10">
            {rows.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/onboarding/submissions/${p.id}`}
                  className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-surface-inset/40"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-fg">{p.full_name}</div>
                    <div className="text-xs text-fg-muted">
                      {p.position ?? 'Role unknown'}
                      {p.contract ? ` · ${p.contract}` : ''}
                      {p.start_date ? ` · starts ${formatDate(p.start_date)}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {p.has_started && p.done === 0 ? (
                      <Pill tone="info" size="sm">In progress</Pill>
                    ) : null}
                    <span className="text-xs text-fg-muted">{p.done}/{p.total}</span>
                    <ChevronRight className="h-4 w-4 text-fg-dim" aria-hidden />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </main>
  );
}
